import { useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { forest, logoRow } from "@/lib/svg";
import SyncScreen from "@/pages/SyncScreen";

export default function Login({ onLogin, onBack }) {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const bannerRef = useRef(null);

  const bgHtml = useRef(forest()).current;
  const logoHtml = useRef(logoRow(1.35)).current;

  const shake = () => {
    setErr(true);
    const b = bannerRef.current;
    if (b) {
      b.classList.remove("shake");
      void b.offsetWidth;
      b.classList.add("shake");
    }
  };

  const doLogin = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { username: u.trim().toLowerCase(), password: p });
      setErr(false);
      setPending(r.data);
    } catch (e) {
      shake();
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter") doLogin();
  };

  if (pending) {
    return (
      <SyncScreen
        name={pending.user.name}
        onDone={() => {
          onLogin(pending.token, pending.user);
          toast.success("Signed in as " + pending.user.name);
        }}
      />
    );
  }

  return (
    <div className="login" data-testid="login-screen">
      <div className="login-bg" dangerouslySetInnerHTML={{ __html: bgHtml }} />
      <div className="login-card">
        <div className="logo-row" dangerouslySetInnerHTML={{ __html: logoHtml }} />
        {onBack && (
          <button className="link" data-testid="login-back-button" style={{ display: "block", margin: "0 auto 18px" }} onClick={onBack}>
            ← Back to summitridgebank.com
          </button>
        )}
        {err && (
          <div className="banner shake" ref={bannerRef} data-testid="login-error-banner">
            Please verify your information and try again.
          </div>
        )}
        <div className="field f-inline">
          <span className="lbl">Username</span>
          <input
            data-testid="login-username-input"
            autoComplete="username"
            value={u}
            onChange={(e) => setU(e.target.value)}
            onKeyDown={onKey}
          />
          <button className="link" data-testid="login-switch-button" onClick={() => setU("")}>
            Switch
          </button>
        </div>
        <div className="field">
          <span className="lbl">Enter your password</span>
          <input
            data-testid="login-password-input"
            type={show ? "text" : "password"}
            autoComplete="current-password"
            value={p}
            onChange={(e) => setP(e.target.value)}
            onKeyDown={onKey}
          />
          <button
            className="eye"
            data-testid="login-toggle-password-button"
            aria-label="Show password"
            onClick={() => setShow((s) => !s)}
          >
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#a9d64b" strokeWidth="1.8">
              <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" />
              <circle cx="12" cy="12" r="2.7" />
            </svg>
          </button>
        </div>
        <div className="row-end">
          <button
            className="link"
            data-testid="login-forgot-button"
            onClick={() => toast("Reset link sent to m•••@ridgecreditfinancial.com")}
          >
            Forgot?
          </button>
        </div>
        <div className="actions">
          <button
            className="passkey"
            data-testid="login-passkey-button"
            onClick={() => toast("No passkey enrolled on this device — sign in with your password.")}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#a9d64b" strokeWidth="1.7">
              <path d="M12 11a3 3 0 0 0-3 3v7M12 11a3 3 0 0 1 3 3v2" />
              <path d="M12 11V7.5A4.5 4.5 0 0 1 16.5 3M12 11V7.5A4.5 4.5 0 0 0 7.5 3" />
            </svg>
            Sign in with a passkey
          </button>
          <button className="btn btn-navy" data-testid="login-submit-button" disabled={busy} onClick={doLogin}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}
